/**
 * Where Paiements is, from the URL hash, so every view is a link that can be
 * shared (and that the dashboard's alerts open):
 *
 * - `#payments` is every fee payment, newest first;
 * - `#payments?provider=orange_money&status=failed&from=2026-09-01&duplicates=true` filtered;
 * - `#payments?approvals=pending` shows the approval requests instead;
 * - `#payments/<platformFeePaymentId>` opens one payment beside the list, keeping the filters.
 */
import {
  APPROVAL_REQUEST_STATUSES,
  FEE_PAYMENT_STATUSES,
  PAYMENT_PROVIDERS,
  type ApprovalRequestStatus,
  type FeePaymentStatus,
  type ListPaymentsQuery,
  type PaymentProvider,
} from "@wallyo/contracts/console/payments";

export type PaymentFilters = {
  provider: PaymentProvider | null;
  status: FeePaymentStatus | null;
  /** YYYY-MM-DD, inclusive. */
  from: string | null;
  to: string | null;
  likelyDuplicates: boolean;
  /** One pro's payments. */
  workerId: string | null;
};

export const ALL_PAYMENTS: PaymentFilters = {
  provider: null,
  status: null,
  from: null,
  to: null,
  likelyDuplicates: false,
  workerId: null,
};

export type PaymentsRoute = {
  filters: PaymentFilters;
  /** Set: the approval requests with this status are listed instead of payments. */
  approvals: ApprovalRequestStatus | null;
  platformFeePaymentId: string | null;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

const oneOf = <T extends string>(values: readonly T[], value: string | null): T | null =>
  value !== null && (values as readonly string[]).includes(value) ? (value as T) : null;

const day = (value: string | null): string | null => (value && DAY.test(value) ? value : null);

export function parsePaymentsHash(hash: string): PaymentsRoute {
  const rest = hash.replace(/^#\/?payments/, "");
  const [path = "", search = ""] = rest.split("?", 2);
  const params = new URLSearchParams(search);
  let platformFeePaymentId: string | null = null;
  try {
    platformFeePaymentId = decodeURIComponent(path.replace(/^\/+/, "").split("/")[0] ?? "").trim() || null;
  } catch {
    platformFeePaymentId = null;
  }
  return {
    filters: {
      provider: oneOf(PAYMENT_PROVIDERS, params.get("provider")),
      status: oneOf(FEE_PAYMENT_STATUSES, params.get("status")),
      from: day(params.get("from")),
      to: day(params.get("to")),
      likelyDuplicates: params.get("duplicates") === "true" || params.get("likelyDuplicates") === "true",
      workerId: params.get("workerId")?.trim() || null,
    },
    approvals: oneOf(APPROVAL_REQUEST_STATUSES, params.get("approvals")),
    platformFeePaymentId,
  };
}

/** The hash for the list (or the approval requests) and a payment open beside it. */
export function paymentsHref(
  filters: PaymentFilters,
  platformFeePaymentId: string | null = null,
  approvals: ApprovalRequestStatus | null = null,
): string {
  const params = new URLSearchParams();
  if (approvals) params.set("approvals", approvals);
  if (filters.provider) params.set("provider", filters.provider);
  if (filters.status) params.set("status", filters.status);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.likelyDuplicates) params.set("duplicates", "true");
  if (filters.workerId) params.set("workerId", filters.workerId);
  const query = params.toString();
  const base = platformFeePaymentId ? `#payments/${encodeURIComponent(platformFeePaymentId)}` : "#payments";
  return query ? `${base}?${query}` : base;
}

/** What the list asks the server for. */
export function paymentsRequest(filters: PaymentFilters, cursor?: string | null): ListPaymentsQuery {
  const request: ListPaymentsQuery = {};
  if (filters.workerId) request.workerId = filters.workerId;
  if (filters.provider) request.provider = filters.provider;
  if (filters.status) request.status = filters.status;
  if (filters.from) request.from = filters.from;
  if (filters.to) request.to = filters.to;
  if (filters.likelyDuplicates) request.likelyDuplicates = true;
  if (cursor) request.cursor = cursor;
  return request;
}
